import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CTimePicker,
} from '@coreui/react-pro'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import FixtureService from 'src/service/FixtureService'
import RoundService from 'src/service/RoundService'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  findRoundWindow,
  isWithinRoundWindow,
  roundMaxDate,
  roundMinDate,
  roundOptionLabel,
  roundRangeMessage,
} from 'src/utils/roundWindow'
import CIcon from '@coreui/icons-react'
import { cilPlus, cilTrash } from '@coreui/icons'

const AddFormForLeagueIndoorFixture = ({
  visible,
  onClose,
  activePage,
  columnFilter,
  columnSorter,
  itemsPerPage,
  fetchData,
}) => {
  const [loader, setLoader] = useState(false)
  const [matchTypeList, setMatchTypeList] = useState()
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [teamList, setTeamList] = useState([])
  const [errorMessages, setErrorMessages] = useState([])
  const [teamRowsError, setTeamRowsError] = useState([])
  const [teamRows, setTeamRows] = useState([{ home: '', away: '' }])
  const [lockoutStartTime, setLockoutStartTime] = useState('')
  const [lockoutEndTime, setLockoutEndTime] = useState('')
  const [roundList, setRoundList] = useState([])
  const [selectedRoundId, setSelectedRoundId] = useState('')
  const keepOpenRef = useRef(false)

  const roundWindow = useMemo(
    () => findRoundWindow(roundList, selectedRoundId),
    [roundList, selectedRoundId],
  )
  const outOfRoundMessage = roundRangeMessage(roundWindow)

  const validationSchema = Yup.object().shape({
    match_type: Yup.string().required('Match type is required'),
    start_date: Yup.string()
      .required('Start date is required')
      .test('start-date-in-round', outOfRoundMessage, (value) =>
        isWithinRoundWindow(roundWindow, value, null),
      ),
    end_date: Yup.string()
      .required('End date is required')
      .test('end-date-in-round', outOfRoundMessage, (value) =>
        isWithinRoundWindow(roundWindow, value, null),
      ),
    start_time: Yup.string()
      .required('Start time is required')
      .test('start-time-in-round', outOfRoundMessage, function (value) {
        return isWithinRoundWindow(roundWindow, this.parent.start_date, value)
      }),
    end_time: Yup.string()
      .required('End time is required')
      .test('end-time-in-round', outOfRoundMessage, function (value) {
        return isWithinRoundWindow(roundWindow, this.parent.end_date, value)
      }),
  })

  useEffect(() => {
    if (visible) {
      TeamService.getTeamListByGrade().then((res) => {
        if (res.status === 200) {
          setTeamList(res.data)
          setMatchTypeList(res.matchTypeList)
        }
      })
      RoundService.getRoundDropDown().then((res) => {
        if (res.status === 200) {
          setRoundList(res.data || [])
        }
      })
    }
  }, [visible])

  const formik = useFormik({
    initialValues: {
      round_id: '',
      match_type: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      opposition_club: '',
      vanue: '',
      teamRows: [{ home: '', away: '' }],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const rowErrors = []
      teamRows.forEach((row, index) => {
        const err = {}
        if (!row.home) err.home = 'Home team is required'
        if (!row.away) err.away = 'Away team is required'
        if (row.home && row.away && row.home === row.away) err.same = 'Home and Away cannot be same'
        rowErrors[index] = err
      })
      setTeamRowsError(rowErrors)

      const hasErrors = rowErrors.some((e) => Object.keys(e).length > 0)
      if (hasErrors) {
        return
      }

      const payload = {
        ...data,
        teamRows,
        start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
        end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        start_time: lockoutStartTime,
        end_time: lockoutEndTime,
      }
      setLoader(true)
      FixtureService.saveFixtureForLeagueAndIndoor(payload)
        .then((res) => {
          if (res.success) {
            setErrorMessages([])
            fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const resetFields = (actions) => {
    actions.resetForm()
    setStartDate(null)
    setEndDate(null)
    setLockoutStartTime('')
    setLockoutEndTime('')
    setTeamRows([{ home: '', away: '' }])
    setTeamRowsError([])
    setSelectedRoundId('')
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  const resetDateFields = () => {
    setStartDate(null)
    setEndDate(null)
    setLockoutStartTime('')
    setLockoutEndTime('')
    formik.setValues({
      ...formik.values,
      round_id: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
    })
  }

  // Picking a round prefills the whole window; every field stays editable afterwards, just
  // pinned to that round's dates.
  const handleRoundChange = (event) => {
    const roundId = event.target.value
    setSelectedRoundId(roundId)

    const range = findRoundWindow(roundList, roundId)
    if (!range) {
      resetDateFields()
      return
    }

    const roundStartTime = range.start.format('HH:mm')
    const roundEndTime = range.end.format('HH:mm')
    setStartDate(range.start.toDate())
    setEndDate(range.end.toDate())
    setLockoutStartTime(roundStartTime)
    setLockoutEndTime(roundEndTime)
    formik.setValues({
      ...formik.values,
      round_id: roundId,
      start_date: range.start.format('YYYY-MM-DD'),
      end_date: range.end.format('YYYY-MM-DD'),
      start_time: roundStartTime,
      end_time: roundEndTime,
    })
  }

  const handleStartDate = (event) => {
    setStartDate(event)
    formik.setTouched({ ...formik.touched, start_date: true })
    formik.setFieldValue('start_date', event ? moment(event).format('YYYY-MM-DD') : '')

    // Keep an end date that still sits on or after the new start; only drop it when it doesn't.
    if (endDate && event && moment(endDate).isBefore(moment(event), 'day')) {
      setEndDate(null)
      formik.setFieldValue('end_date', '')
    }
  }

  const handleEndDate = (event) => {
    setEndDate(event)
    formik.setTouched({ ...formik.touched, end_date: true })
    formik.setFieldValue('end_date', event ? moment(event).format('YYYY-MM-DD') : '')
  }

  const handleLockoutStartTime = (event) => {
    const dateFormat = event ? moment(event, ['H:mm']).format('HH:mm') : ''
    setLockoutStartTime(dateFormat)
    formik.setTouched({ ...formik.touched, start_time: true })
    formik.setFieldValue('start_time', dateFormat)
  }
  const handleLockoutEndTime = (event) => {
    const dateFormat = event ? moment(event, ['H:mm']).format('HH:mm') : ''
    setLockoutEndTime(dateFormat)
    formik.setTouched({ ...formik.touched, end_time: true })
    formik.setFieldValue('end_time', dateFormat)
  }

  const addRow = () => {
    setTeamRows([...teamRows, { home: '', away: '' }])
  }

  const deleteRow = (index) => {
    const updated = [...teamRows]
    updated.splice(index, 1)
    setTeamRows(updated)
  }

  const handleTeamChange = (index, field, value) => {
    const updated = [...teamRows]
    updated[index][field] = value
    setTeamRows(updated)
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 720 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Fixture</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Schedule one or more fixtures for a round.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel className="fw-bold" htmlFor="round_id">
              Round
            </CFormLabel>
            <CFormSelect
              aria-label="Select Round"
              name="round_id"
              id="round_id"
              value={selectedRoundId}
              onChange={handleRoundChange}
            >
              <option value="">Select Round</option>
              {roundList.map((item) => (
                <option value={item.id} key={item.id}>
                  {roundOptionLabel(item)}
                </option>
              ))}
            </CFormSelect>
            <div className="text-medium-emphasis small mt-1">
              {roundWindow
                ? `Dates prefilled from Round ${
                    roundWindow.round
                  }. You can change them within ${roundWindow.start.format(
                    'DD/MM/YYYY h:mm A',
                  )} - ${roundWindow.end.format('DD/MM/YYYY h:mm A')}.`
                : 'Optional - picking a round prefills the fixture dates and times.'}
            </div>
          </CCol>

          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="match_type">
              Match Type *
            </CFormLabel>
            <CFormSelect
              aria-label="Select Match Type"
              name="match_type"
              className={
                'form-control' +
                (formik.errors.match_type && formik.touched.match_type ? ' is-invalid' : '')
              }
              value={formik.values.match_type}
              onChange={formik.handleChange}
              id="match_type"
            >
              <option value={0}>Select Match Type</option>
              {matchTypeList &&
                matchTypeList.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.match_type && formik.touched.match_type && (
              <CFormFeedback invalid>{formik.errors.match_type}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="vanue">
              Venue
            </CFormLabel>
            <input
              type="text"
              name="vanue"
              className={
                'form-control' + (formik.errors.vanue && formik.touched.vanue ? ' is-invalid' : '')
              }
              id="vanue"
              placeholder="Venue"
              value={formik.values.vanue}
              onChange={formik.handleChange}
            />
            {formik.errors.vanue && formik.touched.vanue && (
              <CFormFeedback invalid>{formik.errors.vanue}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="start_date">
              Start Date *
            </CFormLabel>
            <CDatePicker
              key={`start-date-${selectedRoundId}`}
              date={startDate}
              minDate={roundMinDate(roundWindow)}
              maxDate={roundMaxDate(roundWindow)}
              className={formik.errors.start_date && formik.touched.start_date ? 'is-invalid' : ''}
              locale="en-US"
              placeholder="Start Date"
              onDateChange={handleStartDate}
              format="dd/MM/yyyy"
              cleaner={false}
            />
            {formik.errors.start_date && formik.touched.start_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="end_date">
              End Date *
            </CFormLabel>
            <CDatePicker
              key={`end-date-${selectedRoundId}`}
              date={endDate}
              minDate={startDate || roundMinDate(roundWindow)}
              maxDate={roundMaxDate(roundWindow)}
              className={formik.errors.end_date && formik.touched.end_date ? 'is-invalid' : ''}
              locale="en-US"
              placeholder="End Date"
              onDateChange={handleEndDate}
              format="dd/MM/yyyy"
              cleaner={false}
            />
            {formik.errors.end_date && formik.touched.end_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="start_time">
              Start Time *
            </CFormLabel>
            <CTimePicker
              locale="en-US"
              time={lockoutStartTime}
              seconds={false}
              className={formik.errors.start_time && formik.touched.start_time ? 'is-invalid' : ''}
              onTimeChange={handleLockoutStartTime}
            />
            {formik.errors.start_time && formik.touched.start_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="end_time">
              End Time *
            </CFormLabel>
            <CTimePicker
              className={formik.errors.end_time && formik.touched.end_time ? 'is-invalid' : ''}
              locale="en-US"
              time={lockoutEndTime}
              seconds={false}
              onTimeChange={handleLockoutEndTime}
            />
            {formik.errors.end_time && formik.touched.end_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_time}
              </CFormFeedback>
            )}
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Home / Away Matchups</h6>
          </CCol>
          <CCol xs={12}>
            {teamRows.map((row, index) => (
              <div className="row g-2 mb-3 align-items-end team-row" key={index}>
                <CCol xs={5} className="form-col">
                  <CFormLabel className="fw-bold">Home Team</CFormLabel>
                  <CFormSelect
                    value={row.home}
                    onChange={(e) => handleTeamChange(index, 'home', e.target.value)}
                    className={teamRowsError[index]?.home ? 'is-invalid' : ''}
                  >
                    <option value="">Select Home Team</option>
                    {teamList?.map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.name} ({item.sn})
                      </option>
                    ))}
                  </CFormSelect>
                  {teamRowsError[index]?.home && (
                    <div className="invalid-feedback d-block">{teamRowsError[index].home}</div>
                  )}
                </CCol>

                <CCol xs={5} className="form-col">
                  <CFormLabel className="fw-bold">Away Team</CFormLabel>
                  <CFormSelect
                    value={row.away}
                    onChange={(e) => handleTeamChange(index, 'away', e.target.value)}
                    className={
                      teamRowsError[index]?.away || teamRowsError[index]?.same ? 'is-invalid' : ''
                    }
                  >
                    <option value="">Select Away Team</option>
                    {teamList?.map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.name} ({item.sn})
                      </option>
                    ))}
                  </CFormSelect>
                  {teamRowsError[index]?.away && (
                    <div className="invalid-feedback d-block">{teamRowsError[index].away}</div>
                  )}
                  {teamRowsError[index]?.same && (
                    <div className="invalid-feedback d-block">{teamRowsError[index].same}</div>
                  )}
                </CCol>

                <CCol xs={2} className="d-flex align-items-end justify-content-start button-col">
                  {index === 0 ? (
                    <CButton color="success" size="sm" onClick={addRow}>
                      <CIcon icon={cilPlus} className="me-1" /> Add
                    </CButton>
                  ) : (
                    <CButton color="danger" size="sm" onClick={() => deleteRow(index)}>
                      <CIcon icon={cilTrash} className="me-1" /> Delete
                    </CButton>
                  )}
                </CCol>
              </div>
            ))}
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Fixture
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddFormForLeagueIndoorFixture
